<template>
  <!-- prettier-ignore -->
  <div class="product">
    <div
      class="product__name"
      v-text="$t(`content.products.${props.productId}.name`)"
    />
    <template v-if="personId">
      <div
        class="product-price"
        :class="{ 'product-price--reduced': product.valid && reduction.price > 0 }"
      >
        <template v-if="hasProductPricing">
          <ovp-product-price
            :class="{
              product__price: true,
            }"
            :prefix="pricePrefix"
            :price="productPricing.price"
            :suffix="$t('product.currency')"
          />

          <the-typography
            v-if="reduction.price > 0"
            type="bodyLargeShort"
            class="product-reduction"
          >
            <div class="reduction-instead">
              {{ $t('product.insteadOf') }}
              <ovp-product-price :price="reduction.price" />
            </div>

            <div
              v-for="(r, index) in reduction.labels"
              :key="index"
              class="reduction__info"
              v-text="getReductionLabel(r, index)"
            />
          </the-typography>
        </template>
      </div>

      <template v-if="product.valid || product.selected">
        <div class="product-options">
          <template v-if="hasProductAccident && hasProductAccidentToggle">
            <div class="product-criteria product-criteria--half">
              <the-typography
                type="bodyLargeShort"
                class="product__title"
              >
                {{ optionTitles.accident.title }}
                <the-icon
                  v-tooltip="optionTitles.accident.tooltip"
                  art="light"
                  html-tooltip
                  name="circle-info"
                />
              </the-typography>
              <ovp-product-accident
                :option="productPricing"
                @select="selectOption"
              />
            </div>
          </template>

          <template v-if="hasProductDuration">
            <div class="product-criteria product-criteria--half">
              <the-typography
                type="bodyLargeShort"
                class="product__title"
              >
                {{ optionTitles.duration.title }}
                <the-icon
                  v-tooltip="optionTitles.duration.tooltip"
                  art="light"
                  html-tooltip
                  name="circle-info"
                />
              </the-typography>
              <ovp-duration
                :category-id="categoryId"
                :options="product.prices"
                :product-id="productId"
                :source="source"
                @select="selectOption"
              />
            </div>
          </template>

          <template v-if="hasProductFranchise">
            <div class="product-criteria product-criteria--full">
              <the-typography
                type="bodyLargeShort"
                class="product__title"
              >
                {{ optionTitles.franchise.title }}
                <the-icon
                  v-tooltip="optionTitles.franchise.tooltip"
                  art="light"
                  html-tooltip
                  name="circle-info"
                />
              </the-typography>

              <ovp-franchise
                inline
                :forced-mobile="product.prices.length > 6"
                :options="product.prices"
                :source="source"
                @select="selectOption"
              />
            </div>
          </template>

          <template v-if="hasVitalProductFranchise">
            <div class="product-criteria product-criteria--full">


              <product-vital-franchise
                inline
                :options="product.prices"
                :source="source"
                @select="selectOption">
                <template #amValue>
                  <div class="options__title">
                    <the-typography type="heading05">
                      <span v-text="optionTitles.amValue.title" />
                      &nbsp;
                      <the-icon
                        v-if="optionTitles.amValue.tooltip"
                        v-tooltip="optionTitles.amValue.tooltip"
                        art="light"
                        html-tooltip
                        name="circle-info"
                      />
                    </the-typography>
                  </div>
                </template>

                <template #gfValue>
                  <div class="options__title">
                    <the-typography type="heading05">
                      <span v-text="optionTitles.gfValue.title" />
                      &nbsp;
                      <the-icon
                        v-if="optionTitles.gfValue.tooltip"
                        v-tooltip="optionTitles.gfValue.tooltip"
                        art="light"
                        html-tooltip
                        name="circle-info"
                      />
                    </the-typography>
                  </div>
                </template>
              </product-vital-franchise>


            </div>
          </template>
        </div>
      </template>

      <the-message
        v-else
        :text="messages"
      />

      <div class="product-actions">
        <ovp-product-action
          :person-id="personId"
          :product="product"
          :selected="product.selected"
          @add="add"
          @remove="remove"
          @search="searchMedicalOffice"
        />
      </div>

      <ovp-doctor
        v-if="product.medicalOffice"
        :person-id="personId"
        :product="product"
        :source="source"
        @search="searchMedicalOffice"
      />
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { events$, loading$ } from '@/services'

import {
  useApplication,
  useBasket,
  useCategories,
  useI18n,
  usePerson,
  usePersonSpecificProduct,
  useProduct,
  useToast,
} from '@/hooks'

import TheMessage from '@/components/Container/TheMessage'
import {
  OvpDoctor,
  OvpProductAction,
  OvpProductPrice,
  OvpProductAccident,
  OvpFranchise,
  OvpDuration,
} from '@/components/Product'
import ProductVitalFranchise from '@/components/Product/ProductVitalFranchise'

import { CATEGORY_TYPE, NOTIFICATION } from '@/config/constants'
import { EVENT_MEDICAL_OFFICE_MODAL_OPEN, EVENT_PRODUCT } from '@/config/events'

// HOOKS
const { isWww } = useApplication()
const { removeProductFromOvpBasket, updateOvpBasket, addProductToOvpBasket, persistBasket } = useBasket()
const { getCategory } = useCategories()
const { te, t } = useI18n()
const { getPerson } = usePerson()
const { getProduct, getProductPrice, selectProductOption, selectProductOptionForCategory } = usePersonSpecificProduct()
const {
  getCategoryIdFromProduct,
  getGroupIdFromProduct,
  products,
  getOptionTitles,
  hasAccidentToggle,
  hasDuration,
  hasFranchise,
  isUpgrade,
} = useProduct()
const { addToast } = useToast()

// INIT
const emit = defineEmits(['added', 'removed', 'update'])
const props = defineProps({
  personId: {
    type: String,
    default: '',
  },

  productId: {
    type: String,
    required: true,
  },

  source: {
    type: String,
    required: true,
  },
})

let router
if (!isWww.value) {
  router = useRouter()
}

// COMPUTED
const categoryId = computed(() => {
  return getCategoryIdFromProduct(props.productId)
})

const groupId = computed(() => {
  return getGroupIdFromProduct(props.productId)
})

const hasProductAccident = computed(() => {
  return optionTitles.value.accident
})

const hasProductAccidentToggle = computed(() => {
  const person = getPerson(props.personId)
  return hasAccidentToggle({
    categoryId: categoryId.value,
    dateOfBirth: person.personData.dateOfBirth,
    kvgContractStartDate: person.kvgContractStartDate,
    product: product.value,
  })
})

const hasProductDuration = computed(() => {
  return hasDuration(product.value) && optionTitles.value.duration
})

const hasProductFranchise = computed(() => {
  return hasFranchise(product.value) && optionTitles.value.franchise
})

const hasVitalProductFranchise = computed(() => {
  const { hasVitalFranchise } = useProduct()
  return hasVitalFranchise(product.value)
})

const optionTitles = computed(() => {
  return getOptionTitles(props.productId, groupId.value, categoryId.value)
})

const hasProductPricing = computed(() => {
  const __product = getProduct(props.personId, props.productId)
  return props.personId && !!__product?.prices
})

const messages = computed(() => {
  let content
  const unavailable = getContent('unavailable')

  if (!categoryId.value) return unavailable

  const category = getCategory(categoryId.value, props.personId)

  content = handleMessages(category)
  if (!content) {
    content = handleMessages(product.value)
  }

  if (!content) {
    content = unavailable
  }

  return content
})

const product = computed(() => {
  const __product = (props.personId && getProduct(props.personId, props.productId)) || {}

  return {
    productId: props.productId,
    valid: false,
    ...products.value[props.productId],
    ...__product,
  }
})

// @TODO duplicated in ProductBox
const pricePrefix = computed(() => {
  return props.product?.showDiscountGroup && !props.product.selected ? t('product.asof') : ''
})

// @TODO from Product Mixin, might be centralised to optimise
const productPricing = computed(() => {
  return getProductPrice(props.personId, props.productId) || { price: 0 }
})

// @TODO from Product Mixin and needs refactor
const reduction = computed(() => {
  const amount = Math.abs(productPricing.value.reduction)
  return {
    price: amount > 0 ? productPricing.value.price + amount : 0,
    labels: productPricing.value?.reductions ?? [],
  }
})

// METHODS
function getReductionLabel(__reduction, i) {
  const type = t(`info.${__reduction.type}`)
  const amount = Math.abs(__reduction.amount).toFixed(2)
  const currency = t('product.currency')
  const separator = reduction.value.labels.length - 1 !== i ? ';' : ''

  return `${type} ${amount} ${currency}${separator}`
}

async function selectOption(option, simulate = false) {
  loading$.start()

  if (!product.value.selected && !router) {
    simulate = true
  }

  try {
    let _basket
    if (categoryId.value === CATEGORY_TYPE.KVG) {
      _basket = await selectProductOptionForCategory(props.personId, categoryId.value, option)
    } else {
      _basket = await selectProductOption({ personId: props.personId, productId: props.productId, option })
    }

    _basket = await updateOvpBasket(_basket, { simulate })
    if (simulate) {
      persistBasket(_basket)
    }

    events$.emit(EVENT_PRODUCT.CHANGED, {
      basket: _basket,
      categoryId: categoryId.value,
      option,
      personId: props.personId,
      productId: props.productId,
      source: props.source,
    })

    emit('update', props.productId)
    loading$.end()

    return _basket
  } catch {
    loading$.failed()
  }
}

function handleMessages(wrapper) {
  return (
    wrapper?.messages
      ?.map(m => {
        return t(`error.${m.key}`)
      })
      .join('; ')
      .trim() || false
  )
}

async function add() {
  loading$.start()
  const mode = isUpgrade(props.productId) ? 'ADD' : 'REPLACE'

  try {
    let __basket = await addProductToOvpBasket(props.personId, props.productId, { mode })
    __basket = await updateOvpBasket(__basket)

    events$.emit(EVENT_PRODUCT.ADDED, {
      basket: __basket,
      categoryId: categoryId.value,
      personId: props.personId,
      productId: product.value.productId,
      source: props.source,
    })

    emit('added', props.productId)
    loading$.end()
  } catch (error) {
    loading$.failed()
    addToast({
      text: error?.messages[0],
      type: NOTIFICATION.ERROR,
    })
  }
}

async function remove() {
  loading$.start()

  try {
    const __basket = await removeProductFromOvpBasket(props.personId, props.productId)

    events$.emit(EVENT_PRODUCT.REMOVED, {
      basket: __basket,
      categoryId: categoryId.value,
      personId: props.personId,
      productId: product.value.productId,
      source: props.source,
    })

    emit('removed', props.productId)
    loading$.end()
  } catch (error) {
    loading$.failed()
    addToast({
      text: error.messages[0],
      type: NOTIFICATION.ERROR,
    })
  }
}

function searchMedicalOffice() {
  events$.emit(EVENT_MEDICAL_OFFICE_MODAL_OPEN, {
    source: props.source,
    personId: props.personId,
    product: product.value,
  })
}

function getContent(key) {
  const productPath = `content.products.${props.productId}`
  return te(`${productPath}.${key}`) ? t(`${productPath}.${key}`) : t(`product.${key}`)
}
</script>

<style name="mobile" scoped>
.product {
  color: var(--c-primary-neutral-1);
}

.product__name {
  font-size: 30px;
  line-height: 35px;
  font-weight: bold;
}

.product-price {
  display: flex;
  flex-direction: column;
  margin: 0 0 30px 0;

  .product__price {
    font-size: 50px;
    line-height: 60px;
  }
}

.product-price--reduced {
  .product__price {
    color: var(--c-secondary-color-1);
  }

  .product-reduction {
    display: flex;
    flex-direction: column;
  }
}

.product-reduction {
  .reduction-instead {
    color: var(--c-secondary-color-1);
    font-weight: bold;
  }

  .reduction__info {
    font-weight: bold;
  }
}

.product-options {
  display: grid;
  grid-gap: 20px;
  margin: 0 0 20px 0;

  .product__title {
    font-weight: bold;
    margin: 0 0 10px 0;
  }
}

.product-actions {
  display: flex;
  width: 100%;
  margin-top: 8px;

  & :deep(.action) {
    justify-content: flex-end;
    flex: 1 1 auto;

    .button {
      font-weight: bold;
    }
  }
}

.doctor {
  margin: 10px -10px -10px -10px;
}

.product :deep(.message ul) {
  padding: 10px 0 10px 40px !important;
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .product-price {
    flex-direction: row;

    .product__price {
      font-size: 50px;
      line-height: 60px;
    }

    .product-reduction {
      flex-direction: column;
      justify-content: flex-end;
      padding: 0 0 6px 10px;
    }
  }

  .product-options {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: repeat(auto-fit, minmax(50px, 1fr));

    .product-criteria--full {
      grid-column: 1 / span 2;
    }
  }

  .product-actions {
    justify-content: flex-end;

    & :deep(.action) {
      flex: 0 0 auto;
    }
  }
}
</style>
